'use client';

import { type FC, useEffect, useRef } from 'react';
import type { ImageSlideType } from '../../types/storyblok';
import { getImageDimensions } from '../AImage/Picture.helpers';
import styles from './image-slide.module.scss';

const scrollTargetIntoView = (e: { currentTarget: HTMLLIElement }) => {
  e.currentTarget.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
    inline: 'center',
  });
};

export const ImageSlide: FC<ImageSlideType & { id: string }> = ({ id, image }) => {
  const ref = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const slide = ref.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const intersectionRatio = entries.find((entry) => entry.target === ref.current)?.intersectionRatio ?? 1;

        slide.style.setProperty('--intersection-ratio', String(intersectionRatio > 0.9 ? 1 : intersectionRatio));
      },
      {
        threshold: Array.from({ length: 11 }, (_, i) => i / 10),
        root: slide.parentElement,
      },
    );

    observer.observe(slide);

    return () => {
      observer.disconnect();
    };
  }, []);

  const [imageWidth, imageHeight] = (image && getImageDimensions(image.filename)) ?? [NaN, NaN];

  return (
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: TODO
    // biome-ignore lint/a11y/useKeyWithClickEvents: TODO
    <li className={styles.slide} id={id} onClick={scrollTargetIntoView} onFocus={scrollTargetIntoView} ref={ref}>
      {/** biome-ignore lint/performance/noImgElement: TODO */}
      <img
        alt={image.alt ?? ''}
        className={styles.image}
        height={imageHeight}
        loading="lazy"
        src={image.filename}
        srcSet={`${image.filename}/m/640x0/ 320w, ${image.filename}/m/1280x0/ 640w, ${image.filename}/m/1920x0/ 960w`}
        width={imageWidth}
      />
    </li>
  );
};
